import { useEffect, useState } from 'react'
import { ModalProps } from '../../types/modal'
import Modal from '../shared/Modal'
import { useTranslation } from 'next-i18next'
import { ArrowLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import RpcSettings from '@components/settings/RpcSettings'
import DisplaySettings from '@components/settings/DisplaySettings'
import PreferredExplorerSettings from '@components/settings/PreferredExplorerSettings'
import { useViewport } from 'hooks/useViewport'
import { IconButton } from '@components/shared/Button'
import AutoConnectSettings from '@components/settings/AutoConnectSettings'
import ReferrerSettings from '@components/settings/ReferrerSettings'

export const SETTINGS_BUTTON_TITLE_CLASSES = 'text-th-fgd-1'

enum SettingsCategories {
  NETWORK = 'network',
  DISPLAY = 'display',
  PREFERENCES = 'preferences',
  REFERRER = 'referrer',
}

const TABS = Object.values(SettingsCategories)

const SettingsModal = ({ isOpen, onClose }: ModalProps) => {
  const { t } = useTranslation('settings')
  const { isDesktop } = useViewport()
  const [activeTab, setActiveTab] = useState<SettingsCategories | null>(
    isDesktop ? TABS[0] : null,
  )

  useEffect(() => {
    if (!activeTab && isDesktop) {
      setActiveTab(TABS[0])
    }
  }, [activeTab, isDesktop])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      fullScreen
      panelClassNames="overflow-y-auto hide-scroll"
    >
      <div className="mx-auto max-w-[1140px] px-6 py-8 lg:py-12">
        <h2 className="mb-6">{t('settings')}</h2>
        <div className="grid grid-cols-12 lg:gap-8">
          {isDesktop || !activeTab ? (
            <div className="col-span-12 space-y-2 lg:col-span-3 2xl:col-span-4">
              {TABS.map((tab) => (
                <TabButton
                  activeTab={activeTab}
                  key={tab}
                  setActiveTab={setActiveTab}
                  title={tab}
                />
              ))}
            </div>
          ) : null}
          {isDesktop || activeTab ? (
            <div className="col-span-12 lg:col-span-9 2xl:col-span-8">
              <TabContent activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}

export default SettingsModal

const TabContent = ({
  activeTab,
  setActiveTab,
}: {
  activeTab: SettingsCategories | null
  setActiveTab: (tab: SettingsCategories | null) => void
}) => {
  const { NETWORK, DISPLAY, PREFERENCES, REFERRER } = SettingsCategories
  switch (activeTab) {
    case NETWORK:
      return (
        <>
          <MobileCategoryHeading
            setActiveTab={setActiveTab}
            title={activeTab}
          />
          <RpcSettings />
        </>
      )
    case DISPLAY:
      return (
        <>
          <MobileCategoryHeading
            setActiveTab={setActiveTab}
            title={activeTab}
          />
          <DisplaySettings />
        </>
      )
    case PREFERENCES:
      return (
        <>
          <MobileCategoryHeading
            setActiveTab={setActiveTab}
            title={activeTab}
          />
          <AutoConnectSettings />
          <PreferredExplorerSettings />
        </>
      )
    case REFERRER:
      return (
        <>
          <MobileCategoryHeading
            setActiveTab={setActiveTab}
            title={activeTab}
          />
          <ReferrerSettings />
        </>
      )
    default:
      return null
  }
}

const TabButton = ({
  activeTab,
  setActiveTab,
  title,
}: {
  activeTab: SettingsCategories | null
  setActiveTab: (tab: SettingsCategories) => void
  title: SettingsCategories
}) => {
  const { t } = useTranslation('settings')
  return (
    <button
      className={`flex w-full items-center justify-between rounded-md bg-th-bkg-2 p-3 focus:outline-none focus-visible:bg-th-bkg-3 md:hover:bg-th-bkg-3 ${
        activeTab === title ? 'bg-th-bkg-3 text-th-fgd-1' : 'text-th-fgd-3'
      }`}
      onClick={() => setActiveTab(title)}
    >
      <span>{t(title)}</span>
      <ChevronRightIcon className="size-5" />
    </button>
  )
}

const MobileCategoryHeading = ({
  setActiveTab,
  title,
}: {
  setActiveTab: (tab: null) => void
  title: SettingsCategories
}) => {
  const { t } = useTranslation('settings')
  return (
    <div className="mb-2 flex items-center lg:hidden">
      <IconButton
        className="shrink-0 text-th-fgd-3"
        hideBg
        size="medium"
        onClick={() => setActiveTab(null)}
      >
        <ArrowLeftIcon className="size-5" />
      </IconButton>
      <h2 className="text-base">{t(title)}</h2>
    </div>
  )
}
